import React from 'react';
import PropTypes from 'prop-types';
import { getTranslation } from 'common/react/utils/translations';
import { SvgIcon } from 'common/react/components/SvgIcon';

/**
 * Renders the MediaThumbnail for a news article.
 *
 * @param {object} props
 * @param {object} props.data - The media data.
 * @returns {JSX.Element} Component.
 */
export const MediaThumbnailFooter = ({ data }) => {
    const competitionReference = {
        label: '',
        logo: '',
        url: '',
        cssClass: '',
        abbr: ''
    };

    // searching for first `tag:` tag
    const articleTag = data.tags.find((tag) => tag.label.includes('tag:'));
    const tagLabel = articleTag?.label.replace('tag:', '');
    const tagUrl = PULSE.app.common.content.getTagUrlFromTranslation(
        articleTag?.label
    );

    const compRef = data.references.find(
        (ref) => ref.type === 'AFL_COMPETITION'
    );
    if (compRef) {
        const compId = compRef.sid;
        competitionReference.label =
            PULSE.app.common.competition.generateCompetitionAbbreviation(
                compId
            );
        competitionReference.logo =
            PULSE.app.common.competition.generateCompetitionLogo(compId);
        competitionReference.url =
            PULSE.app.common.competition.generateCompetitionUrl(compId);
        competitionReference.cssClass =
            competitionReference.label?.toLowerCase();
    }

    const gatherRound = tagLabel === 'Gather Round';
    const finalsHub = tagLabel === 'Finals Hub';
    const modifier = gatherRound
        ? 'gather-round'
        : finalsHub
        ? 'finals-hub'
        : competitionReference.cssClass;

    return (
        <div className={`media-thumbnail__footer`}>
            <div
                className={`media-thumbnail__reference media-thumbnail__reference--${modifier}`}
            >
                {gatherRound ? (
                    <GatherRoundLabel url={tagUrl} />
                ) : finalsHub ? (
                    <FinalsHubLabel url={tagUrl} />
                ) : (
                    <>
                        {competitionReference.label?.length > 0 && (
                            <CompetitionLabel {...competitionReference} />
                        )}

                        {tagLabel && (
                            <>
                                {competitionReference.label && (
                                    <SvgIcon
                                        icon={`chevron-right`}
                                        className={`icon media-thumbnail__chevron`}
                                    />
                                )}
                                <div
                                    className={`media-thumbnail__tag-label media-thumbnail__tag-label--capitalize`}
                                >
                                    {tagUrl ? (
                                        <a
                                            className="media-thumbnail__tag-link"
                                            href={tagUrl}
                                        >
                                            {tagLabel}
                                        </a>
                                    ) : (
                                        tagLabel
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

MediaThumbnailFooter.propTypes = {
    data: PropTypes.object
};

/**
 * Competition Label component
 * Displays competition logo and abbreviation. Optionally also renders link.
 *
 * @param {object} props
 * @param {string} props.label
 * @param {string} props.logo
 * @param {string} props.url
 * @param {string} props.cssClass
 * @returns {HTMLElement}
 */
const CompetitionLabel = ({ label, logo, url, cssClass }) => {
    const competitionLogo = (
        <>
            {logo !== '' ? (
                <div className="media-thumbnail__comp-logo">
                    {logo === 'AFL' ? (
                        <img
                            src={`/resources/${window.RESOURCES_VERSION}/i/elements/afl-logo.png`}
                            className={`media-thumbnail__comp-logo-img icn-${cssClass}`}
                            alt={label + ' logo'}
                        />
                    ) : (
                        <SvgIcon
                            className={`icon icn-${cssClass}`}
                            icon={logo}
                        />
                    )}
                </div>
            ) : null}
            {label ? (
                <span className={`media-thumbnail__comp-label`}>{label}</span>
            ) : null}
        </>
    );

    return (
        <>
            {url ? (
                <a href={url} className="media-thumbnail__comp-container">
                    {competitionLogo}
                </a>
            ) : (
                competitionLogo
            )}
        </>
    );
};
CompetitionLabel.propTypes = {
    label: PropTypes.string,
    logo: PropTypes.string,
    url: PropTypes.string,
    cssClass: PropTypes.string
};

/**
 * Gather Round label
 *
 * @param {object} props
 * @param {string} props.url
 * @returns {ReactFragment}
 */
const GatherRoundLabel = ({ url }) => (
    <>
        <SvgIcon
            icon="gather-round-flag"
            className="media-thumbnail__tag-icon"
        />
        <a className="media-thumbnail__tag-link" href={url}>
            <div className="media-thumbnail__comp-label">
                {getTranslation('label.gather-round')}
            </div>
        </a>
    </>
);
GatherRoundLabel.propTypes = {
    url: PropTypes.string
};

/**
 * Finals Hub label
 *
 * @param {object} props
 * @param {string} props.url
 * @returns {ReactFragment}
 */
const FinalsHubLabel = ({ url }) => (
    <>
        <SvgIcon icon="trophy" className="media-thumbnail__tag-icon" />
        <a className="media-thumbnail__tag-link" href={url}>
            <div className="media-thumbnail__comp-label">
                {getTranslation('label.finals-hub')}
            </div>
        </a>
    </>
);
FinalsHubLabel.propTypes = {
    url: PropTypes.string
};
